

import { faChevronDown, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

const defaultFaqs = [
    {
        question: 'How much time will it take to join the group after payment?',
        answer:
            'Our system usually adds you instantly to the respective group. However, in rare cases, it may take up to 2 hours. If it takes longer, please contact support.',
    },
    {
        question: 'I have made the payment, but it is not reflecting?',
        answer:
            'Please reach out to our support team with a screenshot or transaction ID. We’ll resolve the issue as quickly as possible.',
    },
    {
        question: 'Can I choose which mobile number to use for joining the Telegram group post purchase?',
        answer:
            'Yes! You’ll receive a unique invite link. You can use any Telegram account to join, but note: each link works for one user only.',
    },
    {
        question: 'Is the group accessible on both mobile and desktop?',
        answer:
            'Absolutely. Telegram groups are synced across all your devices. You can access the group from mobile, desktop, or even web.',
    },
    {
        question: 'What if I accidentally leave the Telegram group?',
        answer:
            'You can use the original invite link to rejoin once, but if it’s expired or revoked, you’ll need to contact support.',
    },
    {
        question: 'Are payments refundable if I change my mind?',
        answer:
            'We offer refunds only in cases where access was never granted. Once you join the group, refunds are not applicable.',
    },
    {
        question: 'Can I share my invite link with a friend?',
        answer:
            'Yes. Each invite link is uniquely tied to your purchase and can only be used once. Sharing it will prevent you from joining.',
    },
];

const verificationFaqs = [
    {
        question: 'What happens after I submit my KYC?',
        answer:
            'Once your KYC is submitted, the app completes verification and takes you straight to your access flow.',
    },
    {
        question: 'How long does verification usually take?',
        answer:
            'Most verification flows start immediately. Once KYC is accepted, your access link should be ready in My Groups.',
    },
    {
        question: 'Which mobile number should I use for verification?',
        answer:
            'Use the same mobile number that you want associated with your subscription and verification records so the process stays consistent.',
    },
    {
        question: 'Can I complete the process on mobile or desktop?',
        answer:
            'Yes. You can submit KYC and continue to your access link from either mobile or desktop, as long as you can access the linked account and OTP steps.',
    },
    {
        question: 'What if my access link does not open right away?',
        answer:
            'Your subscription may still be preparing access. Refresh My Groups after a short wait and open the latest link shown there.',
    },
    {
        question: 'What if I need help during verification?',
        answer:
            'If the KYC or access step does not complete, contact support with the phone number used for login so the team can trace the verification attempt quickly.',
    },
];


export default function FaqSection({ mode = "default" }: { mode?: "default" | "verification" }) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const faqs = mode === "verification" ? verificationFaqs : defaultFaqs;

    const toggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <section className="bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary p-8 md:p-10 rounded-2xl shadow-2xl border border-dark-border backdrop-blur-sm">
            {/* Enhanced Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-golden-accent rounded-xl flex items-center justify-center shadow-lg">
                        <FontAwesomeIcon icon={faQuestionCircle} className="text-xl text-black" />
                    </div>
                    <h2 className="text-3xl font-bold text-text-primary font-montserrat">Frequently Asked Questions</h2>
                </div>
                <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                    Get answers to common questions about our subscription service
                </p>
            </div>

            {/* FAQ Items */}
            <div className="space-y-4 max-w-4xl mx-auto">
                {faqs.map((faq, index) => {
                    const isOpen = openIndex === index;
                    return (
                        <div
                            key={index}
                            className={`group bg-gradient-to-br from-dark-secondary to-dark-tertiary rounded-xl border transition-all duration-300 overflow-hidden ${
                                isOpen 
                                    ? 'border-golden-accent/30 shadow-lg shadow-golden-accent/5' 
                                    : 'border-dark-border hover:border-golden-accent/20'
                            }`}
                        >
                            <button
                                className="w-full flex justify-between items-center text-left p-6 focus:outline-none focus:ring-2 focus:ring-golden-accent/20 rounded-xl transition-all duration-200"
                                onClick={() => toggle(index)}
                            >
                                <div className="flex items-center space-x-4">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${
                                        isOpen 
                                            ? 'bg-golden-accent text-black' 
                                            : 'bg-dark-primary text-golden-accent group-hover:bg-golden-accent/10'
                                    }`}>
                                        <span className="text-sm font-bold">{index + 1}</span>
                                    </div>
                                    <span className="text-lg font-medium text-text-primary group-hover:text-golden-accent transition-colors duration-200">
                                        {faq.question}
                                    </span>
                                </div>
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={`text-golden-accent transition-all duration-300 ${
                                        isOpen ? 'rotate-180 scale-110' : 'group-hover:scale-110'
                                    }`}
                                />
                            </button>
                            
                            <div
                                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                                    isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                }`}
                            >
                                <div className="px-6 pb-6">
                                    <div className="pl-12 pr-8">
                                        <div className="bg-dark-primary/50 rounded-lg p-4 border-l-4 border-golden-accent/30">
                                            <p className="text-text-secondary leading-relaxed">
                                                {faq.answer}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Contact Support CTA */}
            <div className="mt-10 text-center">
                <div className="inline-flex items-center space-x-2 px-6 py-3 bg-golden-accent/10 rounded-full border border-golden-accent/20">
                    <span className="text-golden-accent font-medium">Still have questions?</span>
                    <span className="text-text-secondary">Contact our support team</span>
                </div>
            </div>
        </section>
    );
}
